import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useHistory} from 'react-router-dom'
import styled from 'styled-components'

import {useActionApi, useApi, usePersistedApi, usePersistedState} from 'hooks'
import Api, {CandidateStats} from 'api'
import {entities} from 'reduxStore'

import {BackendJob} from 'components/EditJob'
import {Application, ApplicationStatus} from 'components/JobBoardDetails'
import {PurchaseTokensFlow} from 'components/PurchaseTokensFlow';
import CandidateView, {CandidateDetails, InterviewStatus} from 'components/CandidateView'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import Popup from 'components/Popup'
import {TopBarRedesigned} from "components/TopBarRedesigned";
import {ApplicationActions} from "components/CandidateView/ApplicationActions";
import {NoShowConfirmation} from "components/CandidateView/NoShowConfirmation";
import {ShowContactsConfirmation} from "components/CandidateView/ShowContactsConfirmation";
import {CandidateScoreInfo} from "components/CandidateView/CandidateScoreInfo";
import {RequestResumeAction} from "components/CandidateView/RequestResumeAction";
import {ResumePurchaseFail} from "components/CandidateView/ResumePurchaseFail";
import {ShowContactsAction} from "components/CandidateView/ShowContactsAction";
import {UserAccountProps, HYGIENIST_TYPES} from "utils/types";
import { Location } from 'components/BillingLocationSelector';
import { calculateDistance } from 'utils';
import { nativePageShell, nativePageScrollRegion } from 'styles/nativePageLayout';

declare let window: any
declare let cordova: any

interface RecentlyViewedResult {
    recentlyViewed: number;
}

interface HygienistLocationDetails {
    lat: string;
    lon: string;
}

const PageWrapper = styled.div`
  ${nativePageShell}
`;

const PageScroll = styled.div`
  ${nativePageScrollRegion}
`;

export default function CandidatePage() {
    const history = useHistory()
    const {job}: { job: BackendJob } = history.location.state as any

    const [application, setApplication] = usePersistedState<Application>({
        entityName: entities.dentistCandidateApplication
    })
    const [applicationActionsPopupOpened, setApplicationActionsPopupOpened] = useState(false)
    const [requestResumePopupOpened, setRequestResumePopupOpened] = useState(false)
    const [noShowPopupOpened, setNoShowPopupOpened] = useState(false)
    const [resumePurchaseFailPopupOpened, setResumePurchaseFailPopupOpened] = useState(false)
    const [showContactsConfirmationPopupOpened, setShowContactsConfirmationPopupOpened] = useState(false);
    const [showContactsPopupOpened, setShowContactsPopupOpened] = useState(false);
    const [scoreInfoPopupOpened, setScoreInfoPopupOpened] = useState(false)
    const [tokensPurchaseOpened, setTokensPurchaseOpened] = useState(false)
    const [recentlyViewedResume, setRecentlyViewedResume] = useState(0)
    const [
        candidateStats,
        candidateStatsLoading,
        candidateStatsError,
        getHygienistStatsById
    ] = useApi({
        apiMethod: Api.getHygienistStatsById,
        initialValue: {} as CandidateStats
    })

    const [
        hygienistLocation,
        hygienistLocationLoading,
        hygienistLocationError,
        getHygienistLocationDetails
    ] = useApi({
        apiMethod: Api.getHygienistLocationDetails,
        initialValue: null,
    });

    const openUrl = (url: string) => {
        if (!url) return;

        if (window.Cordova) {
            cordova.InAppBrowser.open(url, '_system', 'location=yes');
        } else {
            window.open(url, '_blank', 'location=yes');
        }
    };

    // Refactored: Extract navigation state to avoid duplication
    const getJobBoardCandidateNavigationState = useCallback(() => ({
        pathname: "jobBoardCandidate",
        state: {
            applicationId: application.applicationId,
            candidateShortCode: application.name,
            matchScore: candidateStats.hygienistScore,
            starScore: candidateStats.starScore !== undefined ? candidateStats.starScore : application.rating,
            ratingsNum: candidateStats.ratings_num
        }
    }), [application.applicationId, application.name, application.rating, candidateStats.hygienistScore, candidateStats.starScore, candidateStats.ratings_num]);

    const onViewCandidateProfile = () => {
        history.push(getJobBoardCandidateNavigationState());
    }

    const [dentistUser, dentistUserLoading, dentistUserError, getDentistUser] = usePersistedApi({
        apiMethod: Api.getUser,
        entityName: entities.user,
        initialValue: {} as UserAccountProps,
    })
    const [billingLocations, locationsLoading, locationsError, getBillingLocations] = usePersistedApi({
        apiMethod: Api.getLocations,
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })
    const [allowSeeContacts, allowSeeContactsLoading, allowSeeContactsError, allowShowInfo] = useApi({
        apiMethod: Api.allowShowInfo,
        initialValue: {allow: application.showContacts || false},
        onSuccess: (data) => {
            if (data && data.allow) {
                setShowContactsPopupOpened(false);
                setShowContactsConfirmationPopupOpened(true);
            }
        },
        onError: () => {
            setShowContactsPopupOpened(false);
        }
    })
    const [purchaseLoading, purchaseError, purchaseResume] = useActionApi({
        apiMethod: Api.purchaseResume,
        onSuccess: (profileDataFromApi: any) => {
            const updatedAppFlags = {
                ...application,
                resumeViewed: 1,
                // Persist hireResumeUrl if it's provided
                ...(profileDataFromApi.hireResumeUrl ? { hireResumeUrl: profileDataFromApi.hireResumeUrl } : {})
            };
            setApplication(updatedAppFlags);

            const jobType = job?.jobType;
            const isSpecialist = jobType === HYGIENIST_TYPES.FRONT_DESK || jobType === HYGIENIST_TYPES.ASSOCIATE_DENTIST;

            if (isSpecialist && profileDataFromApi.hireResumeUrl) {
                openUrl(profileDataFromApi.hireResumeUrl);
                return;
            }

            // Default case for Hygienist/Assistant - use refactored navigation
            history.push(getJobBoardCandidateNavigationState());

            if (profileDataFromApi.showContacts !== undefined && !profileDataFromApi.showContacts) {
                setShowContactsPopupOpened(true);
            }
        },
        onError: () => {
            setResumePurchaseFailPopupOpened(true)
        }
    })

    useEffect(() => {
        getDentistUser({})
        getBillingLocations({})
    }, [getDentistUser, getBillingLocations])

    useEffect(() => {
        if (application?.hygienistId) {
            getHygienistLocationDetails(application.hygienistId);
        }
    }, [application?.hygienistId, getHygienistLocationDetails]);

    const [getAllApplicationsLoading, getAllApplicationsError, getAllApplications] = useActionApi({
        apiMethod: Api.getApplicationsForHireJob,
        onSuccess: (applications: Application[]) => {
            const updatedApplication = applications.find((app: Application) => app.applicationId === application.applicationId);
            
            if (updatedApplication) {
                // Preserve hireResumeUrl if it exists in current state but not in the update
                setApplication({
                    ...updatedApplication,
                    hireResumeUrl: updatedApplication.hireResumeUrl || application.hireResumeUrl
                });
            }
        },
        onError: () => {
            console.error('Error fetching applications');
        }
    });

    useEffect(() => {
        if (job.id !== undefined) {
            getAllApplications(job.id);
        }
    }, [job.id]);

    useEffect(() => {
        const unsubscribe = history.listen(() => {
            // T2-6730: MemoryRouter paths are bare ('candidate'), not '/candidate/...'
            if (history.location.pathname.includes('candidate') && job.id !== undefined) {
                getAllApplications(job.id);
            }
        });
        return () => {
            unsubscribe();
        };
    }, [history, getAllApplications, job.id]);

    const [
        applicationLoading,
        applicationError,
        updateApplication
    ] = useActionApi({
        apiMethod: Api.updateApplication,
        onSuccess: useCallback(
            (updatedData: Application) => {
                setApplication({
                    ...application,
                    ...updatedData
                })
            },
            [setApplication, application]
        )
    })

    const onAllowShowInfo = (newStatus: boolean) => {
        allowShowInfo({ApplicationId: application.applicationId, allow: newStatus})
        // T2-6730: keep Overview checkbox in sync immediately (Resume may also
        // flip consent; derive display from application.showContacts too).
        setApplication({
            ...application,
            showContacts: newStatus,
        })
    }

    const updateStatus = async (status: ApplicationStatus) => {
        await updateApplication({
            applicationId: application.applicationId,
            newData: {
                status,
            }
        })
    }

    const onShortlistCandidate = async () => {
        await updateStatus(ApplicationStatus.shortlisted);
    }

    const onDeclineCandidate = async () => {
        await updateStatus(ApplicationStatus.declined);
    }

    useEffect(() => {
        getHygienistStatsById({ 
            hygienistId: application.hygienistId, 
            applicationId: application.applicationId 
        })
        if (application.status === 0) {
            updateStatus(1)
        }
    }, [getHygienistStatsById, application.hygienistId, application.status])

    const candidateDetails = useMemo(() => {
        const workHistory =
            candidateStats.office_completed_num === 0 || candidateStats.office_completed_num === null
                ? 'This candidate has not previously worked at your office'
                : candidateStats.lastScore === 0
                    ? 'This candidate has worked at your office previously, but you didn\'t rate them'
                    : candidateStats.lastScore >= 3
                        ? 'This candidate has worked at your office previously and you indicated you liked them.'
                        : 'This candidate has worked at your office previously and you indicated you were not happy with them.'

        const interviewReliability =
            candidateStats.missedInterviewCount === 1
                ? `This candidate has missed ${candidateStats.missedInterviewCount} scheduled interview.`
                : candidateStats.missedInterviewCount > 0
                    ? `This candidate has missed ${candidateStats.missedInterviewCount} scheduled interviews.`
                    : 'This candidate has never missed a scheduled interview.'

        const officeLocation = billingLocations.find(loc => loc.id === job?.locationId);

        let distanceInfo: string | undefined = undefined;

        if (officeLocation?.lat && officeLocation?.lon && hygienistLocation?.lat && hygienistLocation?.lon && officeLocation.country !== undefined) {
            try {
                const officeLat = typeof officeLocation.lat === 'number' ? officeLocation.lat : parseFloat(officeLocation.lat);
                const officeLon = typeof officeLocation.lon === 'number' ? officeLocation.lon : parseFloat(officeLocation.lon);
                const candidateLat = parseFloat(hygienistLocation.lat);
                const candidateLon = parseFloat(hygienistLocation.lon);

                if (!isNaN(officeLat) && !isNaN(officeLon) && !isNaN(candidateLat) && !isNaN(candidateLon)) {
                    let distance = calculateDistance(officeLat, officeLon, candidateLat, candidateLon);
                    const unit = officeLocation.country === 0 ? 'km' : 'mi';

                    if (unit === 'mi') {
                        distance *= 0.621371;
                    }
                    const roundedDistance = Math.round(distance);
                    distanceInfo = `This candidate's home address is approximately ${roundedDistance}${unit} from your office.`;
                }
            } catch (e) {
                console.error("Error calculating distance:", e);
            }
        }

        const baseDetails = Number.isFinite(candidateStats.hygienistScore)
            ? {
                score: candidateStats.hygienistScore,
                reviewsCount: candidateStats.ratings_num,
                workHistory,
                interviewReliability,
            } as CandidateDetails
            : ({} as CandidateDetails);
        
        return { ...baseDetails, distanceInfo };
    }, [candidateStats, billingLocations, job?.locationId, hygienistLocation])

    const onSetInterviewStatus = async (newStatus: InterviewStatus) => {
        await updateApplication({
            applicationId: application.applicationId,
            newData: {
                interviewStatus: newStatus
            }
        })
        getHygienistStatsById({ hygienistId: application.hygienistId, applicationId: application.applicationId })
        onApplicationActionsPopupClose()
    }

    const onApplicationActionsPopupClose = () => {
        setApplicationActionsPopupOpened(false);
    }

    const onNoShowButtonClick = () => {
        onApplicationActionsPopupClose()
        setNoShowPopupOpened(true)
    };

    const onCloseNoShowPopup = () => {
        setNoShowPopupOpened(false);
    }

    const onConfirmNoShowPopup = async () => {
        await onSetInterviewStatus(InterviewStatus.noshow);
        onCloseNoShowPopup();
    }

    const onConfirmResumePurchaseFailPopup = () => {
        onCloseResumePurchaseFailPopup();
        setTokensPurchaseOpened(true);
    }

    const onCloseScoreInfoPopup = () => {
        setScoreInfoPopupOpened(false);
    }

    const onLinkCandidateScoreInfoPopup = () => {
        window.Cordova
            ? cordova.InAppBrowser.open(
                'https://support.tempstars.com/hc/en-us/articles/360047266133-Recommendation-Score',
                '_system',
                'location=yes'
            )
            : window.open(
                'https://support.tempstars.com/hc/en-us/articles/360047266133-Recommendation-Score',
                '_blank',
                'location=yes'
            )
    }

    const onCloseShowContactsConfirmationPopup = () => {
        setShowContactsConfirmationPopupOpened(false);
    }

    const onCloseShowContactsPopup = () => {
        setShowContactsPopupOpened(false);
    }

    const onConfirmShowContactsPopup = () => {
        onAllowShowInfo(true);
    }

    const onConfirmResumeActionPopup = async () => {
        onCloseRequestResumePopup();
        // T2-6507: validate the token balance on the frontend before calling the
        // backend (which returns a handled 400 "Not enough tokens"). When the office
        // has no tokens, show the purchase prompt directly instead of firing a request
        // that will fail. If the balance is unknown, fall back to the backend as the
        // source of truth. Regional free-period views and already-viewed (3-month)
        // resumes remain server-authoritative free paths and must not be blocked by
        // the office's token balance (staging review on T2-6644/6645/6572 PR).
        const tokenBalance = Number((dentistUser as any)?.TokenAmount);
        const shouldSpendToken = !job.freePeriod && recentlyViewedResume !== 1;
        if (shouldSpendToken && Number.isFinite(tokenBalance) && tokenBalance < 1) {
            setResumePurchaseFailPopupOpened(true);
            return;
        }
        await purchaseResume(application.applicationId)
    }

    const onCloseRequestResumePopup = () => {
        setRequestResumePopupOpened(false);
    }

    const onCloseTokensPurchasePopup = () => {
        setTokensPurchaseOpened(false)
    }

    const onSuccessTokensPurchasePopup = async () => {
        onCloseTokensPurchasePopup();
        getDentistUser({})
        await purchaseResume(application.applicationId)
    }

    const onCloseResumePurchaseFailPopup = () => {
        setResumePurchaseFailPopupOpened(false)
    }

    const [recentlyViewedResponseLoading, recentlyViewedResponseError, recentlyViewedResponse] = useActionApi({
        apiMethod: Api.checkRecentlyViewed,
        onSuccess: (result: RecentlyViewedResult) => {
            const { recentlyViewed } = result 
            setRecentlyViewedResume(recentlyViewed);
        },
        onError: () => {
            setRecentlyViewedResume(0);
        }
    })

    const onRequestResume = async () => {
        const jobType = job?.jobType;
        const isHygienistOrAssistant = jobType === HYGIENIST_TYPES.HYGIENIST || jobType === HYGIENIST_TYPES.ASSISTANT;

        // Guard Clause 1: Handle Hygienist/Assistant immediately
        if (isHygienistOrAssistant) {
            onViewCandidateProfile();
            return;
        }

        // Logic for Admin/Dentist from here on

        // Guard Clause 2: Handle already viewed resume with a URL
        if (application.resumeViewed && application.hireResumeUrl) {
            openUrl(application.hireResumeUrl);
            return;
        }

        // Guard Clause 3: Handle already viewed but missing URL
        if (application.resumeViewed) {
            await purchaseResume(application.applicationId);
            return;
        }

        // Logic for first-time view
        await recentlyViewedResponse(application.applicationId);
        const needsToken = !job.freePeriod && recentlyViewedResume !== 1;

        // T2-6571: an active campaign still needs confirmation so the office sees
        // the required token-free message before the server-authoritative view.
        if (job.freePeriod || needsToken) {
            setRequestResumePopupOpened(true);
        } else {
            await purchaseResume(application.applicationId);
        }
    }

    // Handler for "View Resume" button when resume is already viewed
    const onViewResume = async () => {
        const jobType = job?.jobType;
        const isHygienistOrAssistant = jobType === HYGIENIST_TYPES.HYGIENIST || jobType === HYGIENIST_TYPES.ASSISTANT;

        if (isHygienistOrAssistant) {
            onViewCandidateProfile();
            return;
        }

        if (application.hireResumeUrl) {
            openUrl(application.hireResumeUrl);
            return;
        }

        // Fallback if URL is missing for some reason
        await purchaseResume(application.applicationId);
    }

    return (
        <PageWrapper>
            <TopBarRedesigned
                title="Candidate Overview"
                onBack={() => {
                    history.goBack()
                }}
            />
            <PageScroll>
            <CandidateView
                application={application}
                onScoreInfoButtonClick={() => {
                    setScoreInfoPopupOpened(true)
                }}
                allowShowInfo={!!(allowSeeContacts.allow || application.showContacts)}
                onAllowShowInfoToggle={onAllowShowInfo}
                candidateDetails={candidateDetails}
                onApplicationActionsPopupOpen={() => setApplicationActionsPopupOpened(true)}
                onRequestResume={onRequestResume}
                onShortlistCandidate={onShortlistCandidate}
                onDeclineCandidate={onDeclineCandidate}
                onViewResume={onViewResume}
            />
            </PageScroll>
            <ResumePurchaseFail
                isOpen={resumePurchaseFailPopupOpened}
                onConfirm={onConfirmResumePurchaseFailPopup}
                onCancel={onCloseResumePurchaseFailPopup}
            />
            <Popup opened={noShowPopupOpened} onClose={onCloseNoShowPopup}>
                <NoShowConfirmation
                    onCancel={onCloseNoShowPopup}
                    onConfirm={onConfirmNoShowPopup}
                />
            </Popup>
            <ShowContactsConfirmation
                isOpen={showContactsConfirmationPopupOpened}
                name={application.name}
                onConfirm={onCloseShowContactsConfirmationPopup}
            />
            {tokensPurchaseOpened ? (
                <PurchaseTokensFlow
                    onClose={onCloseTokensPurchasePopup}
                    onSuccess={onSuccessTokensPurchasePopup}
                />
            ) : null}
                <ApplicationActions
                open={applicationActionsPopupOpened} 
                    onClose={onApplicationActionsPopupClose}
                    onSetInterviewStatus={onSetInterviewStatus}
                    onNoShowButtonClick={onNoShowButtonClick}
                currentInterviewStatus={application.interviewStatus}
                />
            <RequestResumeAction
                isOpen={requestResumePopupOpened}
                onConfirm={onConfirmResumeActionPopup}
                onCancel={onCloseRequestResumePopup}
                freePeriod={job.freePeriod}
                isRecentlyViewed={recentlyViewedResume === 1}
            />
            <CandidateScoreInfo
                isOpen={scoreInfoPopupOpened}
                onConfirm={onCloseScoreInfoPopup}
                onLink={onLinkCandidateScoreInfoPopup}
                onClose={onCloseScoreInfoPopup}
            />
            <ShowContactsAction
                isOpen={showContactsPopupOpened}
                onConfirm={onConfirmShowContactsPopup}
                onCancel={onCloseShowContactsPopup}
            />
            <FixedLoader
                loading={candidateStatsLoading || purchaseLoading || applicationLoading || allowSeeContactsLoading || recentlyViewedResponseLoading || getAllApplicationsLoading || hygienistLocationLoading}
            />
            <FixedError error={candidateStatsError || applicationError || allowSeeContactsError || recentlyViewedResponseError || getAllApplicationsError || hygienistLocationError}/>
        </PageWrapper>
    )
}
